import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { InlineLoader } from 'cat-ecommerce-alloy';
import { replaceTokensInUrl, isEmpty } from '../../../../utils';
import { navigateToUrl } from '../../../../services/windowService';
import links from '../../../../constants/links';
import {
  IS_PM,
  CATCORP_ESITE_URL,
  UOM
} from '../../../../constants/commonConstants';
import { useSelector, useDispatch } from 'react-redux';
import WidgetTitle from './WidgetTitle';
import EquipmentImageWithDescription from './EquipmentImageWithDescription';
import ServiceOptionsDropdown from './ServiceOptionsDropdown';
import FooterLinksAndButtons from './FooterLinksAndButtons';
import EquipmentRangeDropdown from './EquipmentRangeDropdown';
import useAnalytics from '../../../../hooks/useAnalytics/useAnalytics';
import cx from 'classnames';
import { LIST_SELECT_PM_KIT } from '../../analytics/analyticsConstants';
import {
  loadSSROBySerial,
  loadPMKitEquipmentDetails
} from '../../../../store/mlp/actions';
import pmStyles from './PMKitWidget.scss';
import { getUnitOfMeasureCookie } from '../../UnitOfMeasureToggle/utils';

const PMKitWidget = ({
  selectedEquipment,
  myEquipmentList,
  storeId,
  widgetIdentifier,
  isCampaignLandingPage,
  isMachineLandingPage,
  openAddDrawer,
  openEditDrawer
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const esiteURL = useSelector(s => s.common?.esiteURL);
  const catalogId = useSelector(s => s.common?.catalogId);
  const langId = useSelector(s => s.common?.langId);
  const defaultUnitOfMeasure = useSelector(s => s.common?.defaultUnitOfMeasure);
  const equipmentList = useSelector(s => s.mlp?.equipmentList);
  const pmKitOptions = useSelector(s => s.mlp?.pmKitOptions);
  const [selectedSerial, setSelectedSerial] = useState();
  const [selectedUnit, setSelectedUnit] = useState(
    getUnitOfMeasureCookie('uom') ??
      (defaultUnitOfMeasure?.toLowerCase() === UOM.metric ? UOM.metric : UOM.us)
  );
  const [selectedPMKit, setSelectedPMKit] = useState();
  const {
    firePMKitImpressionEvent,
    firePMKitProductClickEvent,
    firePMKitFormSubmitEvent
  } = useAnalytics();
  const { PMKIT_SSRO_BUNDLE_PAGE, PMKIT_LEARNMORE_LINK } = links;
  const serialNumber = selectedEquipment?.serialNumber;
  const salesModel = selectedEquipment?.model;
  const modelsList = equipmentList.data?.models || [];
  const serialRangeErrors = useSelector(s => s.errors?.mlp?.equipmentList);
  const pmKitErrors = useSelector(s => s.errors?.mlp?.selfServiceRepairOptions);
  const itemToString = item =>
    item && item.displayValue ? item.displayValue : '';
  const pmKitsList =
    pmKitOptions.data.bundleData?.bundles || pmKitErrors
      ? pmKitOptions.data.bundleData?.bundles
      : [];
  const isDataLoaded =
    (equipmentList.isLoaded && modelsList?.length === 0) ||
    (pmKitOptions.data.loaded && pmKitsList?.length === 0);
  const isCEModel = selectedEquipment.isCEModel;

  useEffect(() => {
    if (serialNumber) {
      dispatch(loadSSROBySerial(serialNumber, salesModel, IS_PM, isCEModel));
    } else {
      selectedEquipment.model &&
        !isCEModel &&
        dispatch(loadPMKitEquipmentDetails());
    }
  }, [dispatch, selectedEquipment, serialNumber, salesModel, isCEModel]);

  const loadServiceOption = serialValue => {
    equipmentList &&
      dispatch(
        loadSSROBySerial(serialValue.value, salesModel, IS_PM, isCEModel)
      );
  };

  const getSerialRangeValue = serialValue => {
    setSelectedSerial(serialValue);
    !isEmpty(serialValue) && loadServiceOption(serialValue);
  };

  const getCATKitValue = ssId =>
    pmKitOptions?.data?.bundleData?.bundles?.find(
      item => item.serviceOptionID === ssId
    );
  const isPmKitsLoadedSuccessfully =
    pmKitOptions.data.loaded && pmKitsList?.length > 0;

  useEffect(() => {
    isPmKitsLoadedSuccessfully &&
      firePMKitImpressionEvent(pmKitOptions, LIST_SELECT_PM_KIT, salesModel);
  }, [
    firePMKitImpressionEvent,
    pmKitOptions,
    salesModel,
    isPmKitsLoadedSuccessfully
  ]);

  if (pmKitErrors) {
    firePMKitFormSubmitEvent();
  }

  const redirectToBundlePage = ssId => {
    const { prefix, low, high } = serialNumber
      ? { prefix: '', low: '', high: '' }
      : equipmentList.data?.models[selectedSerial?.value]?.serialNumberOrPrefix;
    const isCATKitData = getCATKitValue(ssId);
    firePMKitFormSubmitEvent(
      prefix,
      low,
      high,
      isCATKitData,
      isPmKitsLoadedSuccessfully,
      salesModel,
      serialNumber,
      selectedUnit
    );
    const redirectUrl = replaceTokensInUrl(
      PMKIT_SSRO_BUNDLE_PAGE,
      storeId,
      catalogId,
      langId,
      ssId,
      IS_PM,
      serialNumber,
      salesModel,
      low,
      high,
      prefix,
      selectedUnit,
      isCATKitData?.isCATKit
    );
    navigateToUrl(redirectUrl);
  };

  const getPMKitValue = pmKitValue => {
    setSelectedPMKit(pmKitValue);
    !isEmpty(pmKitValue) && redirectToBundlePage(pmKitValue.value);
    !isEmpty(pmKitValue) &&
      firePMKitProductClickEvent({
        products: pmKitValue,
        list: LIST_SELECT_PM_KIT,
        dealerStore: storeId
      });
  };

  let serialNumberSection;
  if (serialNumber) {
    serialNumberSection = (
      <h6>
        {salesModel} {serialNumber}
      </h6>
    );
  } else {
    serialNumberSection = equipmentList.isEquipmentLoading ? (
      !isCEModel && <InlineLoader />
    ) : (
      <EquipmentRangeDropdown
        modelsList={modelsList}
        itemToString={itemToString}
        getSerialRangeValue={getSerialRangeValue}
        selectedSerial={selectedSerial}
        serialRangeErrors={serialRangeErrors}
      />
    );
  }

  const pmKitReplacedTokensInUrl = esiteURL
    ? replaceTokensInUrl(PMKIT_LEARNMORE_LINK, esiteURL)
    : replaceTokensInUrl(PMKIT_LEARNMORE_LINK, CATCORP_ESITE_URL);
  const pmKitLearnMoreLink = decodeURIComponent(pmKitReplacedTokensInUrl);

  return (
    <div className={`${pmStyles['pmkit-widget__container']} container p-0`}>
      <WidgetTitle isCampaignLandingPage={isCampaignLandingPage} />
      {isCEModel && !serialNumber && <p>{t('ADD_SERIAL_NUMBER')}</p>}
      {isDataLoaded ? (
        <p>{t('PM_KIT_UI2')}</p>
      ) : (
        <div className="pmKit-content__section">
          {(!isCEModel || serialNumber) && (
            <EquipmentImageWithDescription
              isMachineLandingPage={isMachineLandingPage}
              selectedEquipment={selectedEquipment}
              selectedSerial={selectedSerial}
              serialNumber={serialNumber}
              isPmKitsLoadedSuccessfully={isPmKitsLoadedSuccessfully}
              salesModel={salesModel}
            />
          )}

          <div
            className={cx(pmStyles['pmkit-model'], {
              'mt-0': !serialNumber && isCEModel
            })}
          >
            {serialNumberSection}
            {(selectedSerial || serialNumber) && (
              <ServiceOptionsDropdown
                selectedUnit={selectedUnit}
                setSelectedUnit={setSelectedUnit}
                pmKitOptions={pmKitOptions}
                itemToString={itemToString}
                pmKitsList={pmKitsList}
                pmKitErrors={pmKitErrors}
                getPMKitValue={getPMKitValue}
                selectedPMKit={selectedPMKit}
              />
            )}
          </div>
        </div>
      )}
      <FooterLinksAndButtons
        isDataLoaded={isDataLoaded}
        isMachineLandingPage={isMachineLandingPage}
        pmKitLearnMoreLink={pmKitLearnMoreLink}
        serialNumber={serialNumber}
        myEquipmentList={myEquipmentList}
        storeId={storeId}
        selectedEquipment={selectedEquipment}
        widgetIdentifier={widgetIdentifier}
        isCEModel={isCEModel}
        openAddDrawer={openAddDrawer}
        openEditDrawer={openEditDrawer}
      />
    </div>
  );
};

PMKitWidget.propTypes = {
  selectedEquipment: PropTypes.shape({
    unValidatedEquipmentId: PropTypes.string,
    model: PropTypes.string,
    assetId: PropTypes.string,
    productId: PropTypes.string,
    equipmentFamily: PropTypes.string,
    uniqueIndex: PropTypes.string,
    serialNumber: PropTypes.string,
    validatedAsset: PropTypes.bool,
    imageURL: PropTypes.string,
    isCEModel: PropTypes.bool
  }),
  myEquipmentList: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.string]))
  ),
  storeId: PropTypes.string,
  widgetIdentifier: PropTypes.string,
  isCampaignLandingPage: PropTypes.bool,
  isMachineLandingPage: PropTypes.bool,
  openAddDrawer: PropTypes.func,
  openEditDrawer: PropTypes.func
};

export default PMKitWidget;
